import classNames from 'classnames';
import ReloadIcon from '../../../assets/reload.svg?react';
import FJUtil from '../../../util/FJUtil';
import {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
    useRef,
    useCallback,
    type CSSProperties,
    type ReactNode,
    type MouseEvent,
    type ForwardedRef,
} from 'react';
import styles from './FJImage.module.less';
import FJResourceItemLoading from '../FJResourceItemLoading/FJResourceItemLoading';
import FJConstant from '../../../constants/FJConstant';
import FJEvent from '../../../store/FJEvent';

export interface FJImageHandle {
    refresh: (e?: unknown) => void;
}

export interface FJImageProps {
    imgSrc?: string;
    boxClassName?: string;
    imgClassName?: string;
    loadingClassName?: string;
    isShowLoading?: boolean;
    style?: CSSProperties;
    children?: ReactNode;
    isLoading?: boolean;
    onClick?: (e: MouseEvent<HTMLDivElement>) => void;
    loadCallback?: (status: string) => void;
    onRefresh?: () => void;
    reloadStyle?: CSSProperties;
    addRefreshEvent?: boolean;
    /** 为 true 时 img 仅使用 `imgClassName`，不合并 FJImage 默认 `.img` */
    disableDefaultImgClass?: boolean;
    alt?: string;
    loading?: 'lazy' | 'eager';
    decoding?: 'async' | 'auto' | 'sync';
    'aria-hidden'?: boolean | 'true' | 'false';
}

const FJImageInner = (props: FJImageProps, ref: ForwardedRef<FJImageHandle>) => {
    const {
        imgSrc,
        boxClassName,
        imgClassName,
        loadingClassName,
        isShowLoading = false,
        style,
        children,
        isLoading,
        onClick,
        loadCallback,
        onRefresh,
        reloadStyle,
        addRefreshEvent = false,
        disableDefaultImgClass = false,
        alt = '',
        loading = 'lazy',
        decoding,
        'aria-hidden': ariaHidden,
    } = props;
    const [stateImgUrl, setStateImgUrl] = useState(imgSrc);
    const [stateIsLoadError, setStateIsLoadError] = useState(false);
    const [stateIsLoaded, setStateIsLoaded] = useState(false);
    const stateIsLoading = isShowLoading && !stateIsLoaded && !stateIsLoadError;
    const unsubscribe = useRef<(() => void) | null>(null);
    const _imgRef = useRef<HTMLImageElement | null>(null);

    const _refresh = useCallback(
        (e?: unknown) => {
            if (e) {
                FJUtil.forbidEvent?.(e);
            }
            setStateIsLoadError(false);
            setStateIsLoaded(false);
            const nextUrl = FJUtil.getReloadNewUrl?.(imgSrc ?? '') ?? imgSrc ?? '';
            setStateImgUrl(nextUrl);
            loadCallback?.(FJConstant.LOADING);
            onRefresh?.();
            if (addRefreshEvent) {
                unsubscribe.current?.();
                FJEvent.dispatch(FJConstant.REFRESH_IMAGE, undefined);
            }
        },
        [imgSrc, loadCallback, onRefresh, addRefreshEvent],
    );

    useImperativeHandle(ref, () => ({
        refresh: _refresh,
    }));

    useEffect(() => {
        const el = _imgRef.current;
        if (!el) return;
        el.src = imgSrc ?? '';
        setStateIsLoaded(false);
        return () => {
            FJUtil.releaseImageMemory?.(el);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- 仅挂载时同步 ref / 卸载释放，与历史 JSX 一致

    useEffect(() => {
        if (imgSrc !== stateImgUrl) {
            setStateImgUrl(imgSrc);
            loadCallback?.(FJConstant.LOADING);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 与历史 JSX 一致：仅响应 imgSrc
    }, [imgSrc]);

    const _sourceLoaded = () => {
        setStateIsLoadError(false);
        setStateIsLoaded(true);
        loadCallback?.(FJConstant.SUCCESS);
    };
    const _sourceError = () => {
        setStateIsLoadError(true);
        setStateIsLoaded(false);
        loadCallback?.(FJConstant.ERROR);
        if (addRefreshEvent) {
            unsubscribe.current = FJEvent.subscribe(FJConstant.REFRESH_IMAGE, _refresh);
        }
    };
    const _onClick = (e: MouseEvent<HTMLDivElement>) => {
        if (stateIsLoading || isLoading) {
            return;
        }
        onClick?.(e);
    };

    const imgClass = disableDefaultImgClass ? imgClassName : classNames(styles.img, imgClassName);

    return (
        <div className={classNames(boxClassName)} onClick={_onClick} style={style}>
            <img
                src={stateImgUrl}
                alt={alt}
                className={imgClass}
                onLoad={_sourceLoaded}
                onError={_sourceError}
                style={{ opacity: stateIsLoadError || stateIsLoading ? 0 : 1 }}
                loading={loading}
                decoding={decoding}
                aria-hidden={ariaHidden}
                ref={_imgRef}
            />
            {stateIsLoadError && (
                <div className={styles.reload} style={{ ...reloadStyle }} onClick={_refresh}>
                    <ReloadIcon />
                </div>
            )}
            {children}
            {(stateIsLoading || isLoading) && <FJResourceItemLoading className={loadingClassName} />}
        </div>
    );
};

const FJImage = forwardRef(FJImageInner);
FJImage.displayName = 'FJImage';

export default FJImage;
